/**
 * `/watch` — Posts to Comment (§12.1f, endpoint #71), `?post=<id>` deep-linkable.
 *
 * **`/feed`'s screen with a third scope.** The pill header, the 288px sidebar, the drawer, the
 * 552px card track and the `?post=` preview are the feed's — a caught post is the same post, so it
 * has to look and behave like one — narrowed server-side to what a watch check caught. What is new
 * over `/feed` is the `/saved` search box in the header.
 *
 * Three things worth knowing about this file:
 *
 *  - **The ui-state gate.** `watch:main` loads before the first #71 request (§12.4), so a persisted
 *    scope is never briefly ignored and page one is never fetched twice.
 *  - **The list is allowed to move.** Unlike `/feed` there is no "New posts" pill: `watch.detection`
 *    invalidates `QK.watchFeed` in `sse.ts`, and a fresh catch belongs at the top immediately —
 *    this is a catch log, and being late is the one failure mode it has (§12.2).
 *  - **Search is session-only**, like `/saved`: not in the stored blob, debounced into the query
 *    key rather than fired per keystroke.
 *
 * Mounting `PostPreviewProvider` is the whole of `?post=` support: the popup's id lives in the
 * search params, so a cold `/watch?post=<id>` link (the green `new_post` card, and Telegram's
 * "Open in App" button) opens it with no parsing of our own. "Generate comment" needs nothing here
 * either — it rides the `FeedCard` footer, over the root `CommentChatProvider`.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { FeedCardDTO, ViewRangeState, WatchFiltersState } from '@lw/shared';
import { pageTitle } from '../../app/appTitle';
import { CloseIcon, EmptyState, ErrorState, Spinner, cx } from '../../components';
import {
  useDebouncedCallback,
  useDismissable,
  useDocumentTitle,
  useInView,
  useMediaQuery,
} from '../../lib/hooks';
import { useGroupsQuery, useProfilesQuery } from '../home/useHomeData';
import { NotionSaveProvider } from '../notion/useNotionSave';
import { SettingsModal } from '../settings/SettingsModal';
import {
  useAppTimezone,
  useMediaEnabled,
  useSettingsQuery,
  useTimezoneBootstrap,
} from '../settings/useSettings';
import { FeedCard } from '../view/feed/FeedCard';
import { PostPreviewProvider, usePostPreview } from '../view/PreviewContext';
import { anchorNow, resolveRange, type ViewSort } from '../view/viewState';
import { FeedSidebar } from '../feed/FeedSidebar';
import {
  DEFAULT_OPEN_SECTIONS,
  pruneScope,
  totalFilterCount,
  type FeedSectionId,
} from '../feed/feedState';
import { WatchHeader } from './WatchHeader';
import { toWatchParams } from './watchState';
import { useWatchFeed } from './useWatchFeed';
import { useWatchUiState } from './useWatchUiState';
import '../feed/feed.css';
import './watch.css';

/** Below this the sidebar unmounts and the header grows a Filter trigger (as on `/feed`). */
const SIDEBAR_QUERY = '(min-width: 1100px)';
const SEARCH_DEBOUNCE_MS = 300;

export function WatchPage() {
  useTimezoneBootstrap();
  useDocumentTitle(pageTitle('Posts to Comment'));

  const timezone = useAppTimezone();
  const mediaEnabled = useMediaEnabled();
  const countedPostTypes = useSettingsQuery().data?.countedPostTypes ?? [];
  // One modal, two entrances: the empty state asks for watching, the popup's Notion action for Notion.
  const [settingsTab, setSettingsTab] = useState<'watching' | 'notion' | null>(null);

  const uiState = useWatchUiState();
  const wide = useMediaQuery(SIDEBAR_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Session-only, and owned here rather than by the sidebar so the drawer and the island share it.
  const [openSections, setOpenSections] = useState<ReadonlySet<FeedSectionId>>(
    () => new Set(DEFAULT_OPEN_SECTIONS),
  );

  const profilesQuery = useProfilesQuery();
  const groupsQuery = useGroupsQuery();
  const groups = groupsQuery.data?.groups ?? [];
  // Archived profiles are out of the feed's scope entirely, so they are not offerable either.
  const activeProfiles = (profilesQuery.data?.profiles ?? []).filter(
    (profile) => profile.archivedAt === undefined || profile.archivedAt === null,
  );

  /* ------------------------------------------------------------------ search */

  // Two states on purpose: the field stays responsive while the query key only moves every 300 ms.
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const commitSearch = useDebouncedCallback(
    (next: string) => setCommittedSearch(next),
    SEARCH_DEBOUNCE_MS,
  );

  /* ----------------------------------------------------------- range/params */

  const rangeKey = `${uiState.state.range.preset}|${uiState.state.range.from ?? ''}|${uiState.state.range.to ?? ''}`;
  const now = useMemo(() => anchorNow(), [rangeKey]);
  const resolvedRange = useMemo(() => resolveRange(uiState.state.range, now), [rangeKey, now]);

  const filtersKey = JSON.stringify(uiState.state.filters);
  const sortKey = `${uiState.state.sort.key}|${uiState.state.sort.dir}`;
  const params = useMemo(
    () => toWatchParams(uiState.state, resolvedRange, committedSearch),
    [filtersKey, sortKey, resolvedRange, committedSearch],
  );

  const watch = useWatchFeed(params, uiState.ready);
  const cards = watch.data?.pages.flatMap((page) => page.cards) ?? [];
  // `meta` rides the cursor-less page only, which is always the first one in the cache.
  const meta = watch.data?.pages[0]?.meta;

  /* -------------------------------------------------------------- pruning */

  // A persisted selection can outlive the group it names, or the profile it names can be archived.
  // The server ignores dangling ids regardless; this keeps the *chips* honest. Runs once per load,
  // and only when something actually dangles, so it cannot loop against its own write.
  const prunedRef = useRef(false);
  useEffect(() => {
    if (prunedRef.current || !uiState.ready) return;
    if (!groupsQuery.isSuccess || !profilesQuery.isSuccess) return;
    prunedRef.current = true;
    const next = pruneScope(
      uiState.state.filters,
      new Set(groups.map((group) => group.id)),
      new Set(activeProfiles.map((profile) => profile.id)),
    );
    if (next !== null) uiState.update({ filters: next });
  }, [uiState, groupsQuery.isSuccess, profilesQuery.isSuccess, groups, activeProfiles]);

  /* ---------------------------------------------------------------- render */

  const setFilters = (filters: WatchFiltersState) => uiState.update({ filters });
  const setRange = (range: ViewRangeState) => uiState.update({ range });
  const setSort = (sort: ViewSort) => uiState.update({ sort });
  const toggleSection = (section: FeedSectionId) =>
    setOpenSections((previous) => {
      const next = new Set(previous);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  const filterCount = totalFilterCount(uiState.state.filters);
  const narrowed = filterCount > 0 || committedSearch.trim().length > 0;

  const sidebar = (
    <FeedSidebar
      filters={uiState.state.filters}
      onChange={setFilters}
      groups={groups}
      profiles={activeProfiles}
      mediaEnabled={mediaEnabled}
      countedPostTypes={countedPostTypes}
      openSections={openSections}
      onToggleSection={toggleSection}
    />
  );

  return (
    <NotionSaveProvider onOpenSettings={() => setSettingsTab('notion')}>
      <PostPreviewProvider mediaEnabled={mediaEnabled} timezone={timezone}>
        <div className="feed-screen">
          <WatchHeader
            summaryDisplay={meta?.summaryDisplay}
            search={search}
            onSearchChange={(next) => {
              setSearch(next);
              commitSearch(next);
            }}
            sort={uiState.state.sort}
            onSortChange={setSort}
            range={uiState.state.range}
            resolvedRange={resolvedRange}
            onRangeChange={setRange}
            timezone={timezone}
            now={now}
            onOpenFilters={wide ? null : () => setDrawerOpen(true)}
            filterCount={filterCount}
          />

          <div className="feed-screen__body">
            <div className={cx('feed-layout', !wide && 'feed-layout--narrow')}>
              {wide ? <aside className="feed-layout__side">{sidebar}</aside> : null}

              <main className="feed-layout__main">
                {uiState.error !== null ? (
                  <ErrorState
                    title="Couldn't restore your saved filters"
                    description="The screen is showing the default range and filters. Retrying re-reads what was stored."
                    onRetry={uiState.retry}
                  />
                ) : null}

                <WatchList
                  cards={cards}
                  mediaEnabled={mediaEnabled}
                  isPending={!uiState.ready || watch.isPending}
                  error={watch.isError ? watch.error : null}
                  onRetry={() => void watch.refetch()}
                  retrying={watch.isFetching}
                  hasNextPage={watch.hasNextPage}
                  isFetchingNextPage={watch.isFetchingNextPage}
                  fetchNextPage={() => void watch.fetchNextPage()}
                  empty={
                    narrowed ? (
                      <EmptyState
                        title="No posts match"
                        description="Clear the filters, widen the date range, or search for something else."
                        action={
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => {
                              setFilters({});
                              setSearch('');
                              setCommittedSearch('');
                            }}
                          >
                            Clear
                          </button>
                        }
                      />
                    ) : (
                      <EmptyState
                        title="Nothing caught yet"
                        description="Watching checks the accounts you mark every few minutes and lists their new posts here, so you can comment while it is still early. Turn it on and pick some accounts to start."
                        action={
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => setSettingsTab('watching')}
                          >
                            Open watching settings
                          </button>
                        }
                      />
                    )
                  }
                />
              </main>
            </div>
          </div>

          {!wide && drawerOpen ? (
            <WatchDrawer onClose={() => setDrawerOpen(false)}>{sidebar}</WatchDrawer>
          ) : null}

          <SettingsModal
            open={settingsTab !== null}
            initialTab={settingsTab ?? 'watching'}
            onClose={() => setSettingsTab(null)}
          />
        </div>
      </PostPreviewProvider>
    </NotionSaveProvider>
  );
}

type WatchListProps = {
  cards: FeedCardDTO[];
  mediaEnabled: boolean;
  isPending: boolean;
  error: Error | null;
  onRetry: () => void;
  retrying: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  empty: ReactNode;
};

/** The 552px card track — `/feed`'s, verbatim. Split out so the preview context is above it. */
function WatchList({
  cards,
  mediaEnabled,
  isPending,
  error,
  onRetry,
  retrying,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  empty,
}: WatchListProps) {
  const { openPreview } = usePostPreview();
  const ids = cards.map((card) => card.id);

  const sentinelRef = useInView<HTMLDivElement>(hasNextPage && !isFetchingNextPage, fetchNextPage, {
    rootMargin: '400px',
  });

  return (
    <div className="feed">
      {isPending ? <Spinner size="lg" label="Loading posts…" /> : null}

      {error !== null ? (
        <ErrorState
          title="Couldn't load the posts"
          error={error}
          onRetry={onRetry}
          retrying={retrying}
        />
      ) : null}

      {!isPending && error === null && cards.length === 0 ? empty : null}

      <div className="feed__list">
        {cards.map((card) => (
          <FeedCard
            key={card.id}
            card={card}
            mediaEnabled={mediaEnabled}
            onOpen={(postId) => openPreview(postId, ids)}
            watchActions
          />
        ))}
      </div>

      <div ref={sentinelRef} className="feed__sentinel">
        {isFetchingNextPage ? <Spinner label="Loading more…" /> : null}
        {!hasNextPage && cards.length > 0 ? <span className="muted">End of the list.</span> : null}
      </div>
    </div>
  );
}

/** The sidebar as a left slide-in under the breakpoint — the feed's drawer, verbatim. */
function WatchDrawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useDismissable<HTMLDivElement>(true, onClose);

  return (
    <div className="feed-drawer" role="dialog" aria-label="Filters" aria-modal="true">
      <div className="feed-drawer__scrim" />
      <div className="feed-drawer__panel" ref={ref}>
        <div className="feed-drawer__head">
          <span className="feed-drawer__title">Filters</span>
          <button type="button" className="icon-btn" aria-label="Close filters" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default WatchPage;
